<template>
  <div class="search">
    <VueAutosuggest
      ref="autocomplete"
      :suggestions="suggestions"
      :input-props="inputProps"
      :get-suggestion-value="getSuggestionValue"
      @input="debounceSearch"
      @selected="onSelected"
    >
      <div slot-scope="{ suggestion }" class="suggestion">
        <div class="poster"><Poster :poster="suggestion.item.poster" size="s166" /></div>

        <div class="title">
          <h3>{{ suggestion.item.title }}</h3>
          <Ratings :ratings="suggestion.item.ratings" />
        </div>

        <div v-if="suggestion.item.age_certification" class="tag age">{{ suggestion.item.age_certification }} år</div>
        <Providers :providers="suggestion.item.providers" />
      </div>
    </VueAutosuggest>
  </div>
</template>

<script>
import { VueAutosuggest } from 'vue-autosuggest';
import Ratings from './Ratings';
import Poster from './Poster';
import Providers from './Providers';
import guid from '../utils/guid';

export default {
  name: 'Search',
  components: {
    VueAutosuggest,
    Ratings,
    Poster,
    Providers,
  },

  data() {
    return {
      loading: false,
      selected: null,
      selectedProviders: [],
      selectedGenres: [],
      selectedContentTypes: 'movie',
      selectedMonetizationTypes: [],
      debounce: null,
      debounceMilliseconds: 50,
      country: 'NO',
      suggestions: [],
      suggestionsCount: 0,
      inputProps: {
        id: 'autosuggest__input',
        placeholder: 'Søk etter filmer',
      },
    };
  },

  methods: {
    debounceSearch: function(event) {
      clearTimeout(this.debounce);
      this.debounce = setTimeout(() => {
        this.getItems(event);
      }, this.debounceMilliseconds);
    },

    onSelected: async function(item) {
      if (!item) return;
      this.loading = true;
      await this.getItem(item.item.id);
      this.$emit('item_selected', this.selected[0]);
      this.loading = false;
    },

    getSuggestionValue(suggestion) {
      return suggestion.item.title;
    },

    getItems: async function(query) {
      try {
        this.loading = true;
        //const response = await fetch('/mocks/batman.json');
        const response = await fetch(`https://justwatchapi.herokuapp.com/search/${query}`);
        const { total_results, items } = await response.json();
        this.suggestionsCount = total_results;
        this.suggestions = [{ data: this.mergeSources(items) }] || [];

        this.loading = false;
        return 'Got it!';
      } catch (error) {
        throw new Error(error);
      }
    },

    getItem: async function(query) {
      try {
        this.loading = true;
        //const response = await fetch('/mocks/batmanSingle.json');
        const response = await fetch(`https://justwatchapi.herokuapp.com/movie/id/${query}`);
        const item = await response.json();
        this.selected = this.mergeSources([item]);

        this.loading = false;
        return 'Got it!';
      } catch (error) {
        throw new Error(error);
      }
    },

    mergeSources: function(value) {
      let data = [];

      value.map(item => {
        data.push({
          id: item.id,
          title: item.title,
          type: item.object_type,
          fullPath: item.full_path,
          providers: this.groupBy(item.offers, offer => offer.provider_id),
          releaseData: item.original_release_year,
          poster: item.poster,
          ratings: item.scoring.filter(score => score.provider_type === 'tomato_userrating:meter'),
          ageCertification: item.age_certification,
          backdrops: item.backdrops,
          clips: item.clips,
          credits: item.credits,
          genres: item.genre_ids,
          shortDescription: item.short_description,
          originalReleaseYear: item.original_release_year,
          runtime: item.runtime,
          originalTitle: item.original_title,
        });
      });
      return data;
    },

    groupBy: function(list, keyGetter) {
      if (!list) return;
      const map = new Map();
      list.forEach(item => {
        const key = keyGetter(item);
        const collection = map.get(key);
        const item2 = {
          id: guid(),
          ...item,
        };
        if (!collection) {
          map.set(key, [item2]);
        } else {
          const e = collection.some(c => {
            // console.log(c, item);
            return c.monetization_type === item2.monetization_type;
          });
          if (!e) {
            collection.push(item2);
          }
        }
      });
      return Array.from(map.values());
    },
  },
};
</script>

<style lang="scss">
::placeholder {
  color: white;
  opacity: 0.6;
}
.search {
  flex: 1;

  #autosuggest {
    #autosuggest__input {
      background-color: transparent;
      caret-color: red;
      position: relative;
      padding: 24px;
      padding-left: 100px;
      width: 100%;
      border: 0;
      font-size: 20px;
      outline: none;
      color: white;

      &.autosuggest__input--open {
        background: var(--bg);
      }
    }

    .autosuggest__results-container {
      position: relative;
      width: 100%;

      .autosuggest__results {
        background-color: white;
        font-weight: 300;
        margin: 0;
        position: absolute;
        z-index: 101;
        width: 100%;
        border-bottom-left-radius: 4px;
        border-bottom-right-radius: 4px;
        max-height: 800px;
        overflow-y: scroll;
        padding: 0px;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.24);

        .autosuggest__results-before {
          opacity: 0.5;
          margin-left: 0;
          padding: 12px;
        }

        ul {
          list-style: none;
          padding-left: 0;
          margin: 0;

          &:nth-child(1) > .autosuggest__results-before {
            border-top: none;
          }

          .autosuggest__results-item {
            cursor: pointer;
            background-color: transparent;
            padding: 12px;
            color: var(--bg);
            border-bottom: 1px solid var(--border-color);
            transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
            &:active,
            &:hover,
            &:focus,
            &.autosuggest__results-item--highlighted {
              background-color: var(--bg);
              color: white;
            }
          }
        }
      }
    }
  }

  .suggestion {
    display: flex;
    justify-content: flex-start;
    align-items: center;

    .title {
      flex: 1;
    }
    .poster {
      width: 56px;
      margin-right: 36px;
      @media (max-width: 720px) {
        max-width: 36px;
      }
    }

    .providers {
      .provider {
        @media (max-width: 720px) {
          max-width: 24px;
        }
      }
    }
  }
}
</style>
